import React, { useState, useEffect, useCallback } from 'react';
import styles from './IssueRadarModule.module.css';
import { Project } from '../../types';
import { issueRadarAPI } from '../../services/api';

interface IssueRadarModuleProps {
  project: Project;
}

interface IssueOverview {
  totalSignals: number;
  totalIssues: number;
  activeIssues: number;
  overallRisk: number;
  topIssues: Issue[];
  sentimentTrend: { date: string; sentiment: number; volume: number }[];
  sourceBreakdown: { source: string; count: number }[];
  recentAlerts: any[];
}

interface Issue {
  _id: string;
  title: string;
  topics: string[];
  riskScore: number;
  riskLevel: 'low' | 'attention' | 'elevated' | 'acute';
  lifecycle: 'signal' | 'emerging' | 'public_discussion' | 'crisis';
  scores: {
    volumeGrowth: number;
    sentimentShift: number;
    sourceDiversity: number;
    stakeholderCount: number;
    momentum: number;
  };
  trend: {
    sentimentTrend: { date: string; score: number; count: number }[];
    volumeTrend: { date: string; count: number }[];
  };
  stakeholders: { name: string; type: string }[];
  sourceBreakdown: { source: string; count: number; latestSignal: string }[];
  signalCount: number;
  latestSignalDate: string;
  firstSignalDate: string;
  status: string;
  notes?: string;
}

interface SignalItem {
  _id: string;
  source: string;
  type: string;
  title: string;
  text: string;
  url?: string;
  timestamp: string;
  sentimentScore: number;
  sentimentLabel: string;
  topics: string[];
  keywords: string[];
  stakeholder?: string;
}

const SOURCE_LABELS: Record<string, string> = {
  openraadsinformatie: 'Raadsinformatie',
  woo_documenten: 'Woo Documenten',
  gdelt: 'Nieuws (GDELT)',
  petities: 'Petities',
  rivm: 'RIVM',
  knmi: 'KNMI',
  coosto: 'Social Media',
  manual: 'Handmatig',
  nieuws_rss: 'Nieuws RSS'
};

const LIFECYCLE_LABELS: Record<string, string> = {
  signal: 'Signaal',
  emerging: 'Opkomend',
  public_discussion: 'Publieke Discussie',
  crisis: 'Crisis'
};

const LIFECYCLE_COLORS: Record<string, string> = {
  signal: '#4CAF50',
  emerging: '#FF9800',
  public_discussion: '#FF5722',
  crisis: '#D32F2F'
};

const RISK_COLORS: Record<string, string> = {
  low: '#4CAF50',
  attention: '#FF9800',
  elevated: '#FF5722',
  acute: '#D32F2F'
};

export const IssueRadarModule: React.FC<IssueRadarModuleProps> = ({ project }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'issues' | 'signals'>('overview');
  const [overview, setOverview] = useState<IssueOverview | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [signals, setSignals] = useState<SignalItem[]>([]);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [relatedSignals, setRelatedSignals] = useState<SignalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [signalFilter, setSignalFilter] = useState<string>('all');

  const fetchOverview = useCallback(async () => {
    try {
      const data = await issueRadarAPI.getOverview(project.id);
      setOverview(data);
    } catch (error) {
      console.error('Failed to fetch overview:', error);
    }
  }, [project.id]);

  const fetchIssues = useCallback(async () => {
    try {
      const data = await issueRadarAPI.getIssues(project.id);
      setIssues(data);
    } catch (error) {
      console.error('Failed to fetch issues:', error);
    }
  }, [project.id]);

  const fetchSignals = useCallback(async () => {
    try {
      const params: any = { limit: 50 };
      if (signalFilter !== 'all') params.source = signalFilter;
      const data = await issueRadarAPI.getSignals(project.id, params);
      setSignals(data.signals);
    } catch (error) {
      console.error('Failed to fetch signals:', error);
    }
  }, [project.id, signalFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchOverview(), fetchIssues()]).finally(() => setLoading(false));
  }, [fetchOverview, fetchIssues]);

  useEffect(() => {
    if (activeTab === 'signals') {
      fetchSignals();
    }
  }, [activeTab, fetchSignals]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await issueRadarAPI.sync(project.id);
      await Promise.all([fetchOverview(), fetchIssues()]);
      if (activeTab === 'signals') await fetchSignals();
      if (result) {
        setSyncResult(`${result.newSignals || 0} nieuwe signalen opgehaald, ${result.issuesDetected || 0} issues gedetecteerd`);
      } else {
        setSyncResult('Sync voltooid');
      }
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncResult('Sync mislukt — probeer het later opnieuw');
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncResult(null), 8000);
    }
  };

  const handleIssueClick = async (issue: Issue) => {
    setSelectedIssue(issue);
    try {
      const data = await issueRadarAPI.getIssueDetail(project.id, issue._id);
      setRelatedSignals(data.relatedSignals || []);
    } catch (error) {
      console.error('Failed to fetch issue detail:', error);
    }
  };

  const handleIssueStatusChange = async (issueId: string, status: string) => {
    try {
      await issueRadarAPI.updateIssue(project.id, issueId, { status });
      await fetchIssues();
      if (selectedIssue?._id === issueId) {
        setSelectedIssue(prev => prev ? { ...prev, status } : null);
      }
    } catch (error) {
      console.error('Failed to update issue:', error);
    }
  };

  const getRiskColor = (score: number) => {
    if (score >= 80) return RISK_COLORS.acute;
    if (score >= 60) return RISK_COLORS.elevated;
    if (score >= 30) return RISK_COLORS.attention;
    return RISK_COLORS.low;
  };

  const getRiskClass = (score: number) => {
    if (score >= 80) return styles.riskAcute;
    if (score >= 60) return styles.riskElevated;
    if (score >= 30) return styles.riskAttention;
    return styles.riskLow;
  };

  const getRiskBadgeClass = (level: string) => {
    switch (level) {
      case 'acute': return styles.riskBadgeAcute;
      case 'elevated': return styles.riskBadgeElevated;
      case 'attention': return styles.riskBadgeAttention;
      default: return styles.riskBadgeLow;
    }
  };

  const getSentimentColor = (score: number) => {
    if (score < -0.3) return '#D32F2F';
    if (score < -0.1) return '#FF5722';
    if (score > 0.3) return '#4CAF50';
    if (score > 0.1) return '#8BC34A';
    return '#FFC107';
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>Issue Radar laden...</p>
      </div>
    );
  }

  // Issue Detail View
  if (selectedIssue) {
    return (
      <div className={styles.issueRadar}>
        <div className={styles.issueDetail}>
          <div className={styles.issueDetailHeader}>
            <div>
              <h1 className={styles.issueDetailTitle}>{selectedIssue.title}</h1>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', alignItems: 'center' }}>
                <span className={`${styles.riskBadge} ${getRiskBadgeClass(selectedIssue.riskLevel)}`}>
                  Score: {selectedIssue.riskScore}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#757575' }}>
                  {LIFECYCLE_LABELS[selectedIssue.lifecycle] || selectedIssue.lifecycle}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#757575' }}>
                  {selectedIssue.signalCount} signalen
                </span>
              </div>
            </div>
            <button className={styles.closeButton} onClick={() => setSelectedIssue(null)}>&times;</button>
          </div>

          {/* Component Scores */}
          <div className={styles.card}>
            <h3>Risk Score Componenten</h3>
            <div className={styles.scoreGrid}>
              <div className={styles.scoreItem}>
                <div className={styles.scoreItemLabel}>Volume Groei</div>
                <div className={styles.scoreItemValue}>{Math.round(selectedIssue.scores.volumeGrowth * 100)}%</div>
              </div>
              <div className={styles.scoreItem}>
                <div className={styles.scoreItemLabel}>Sentiment Shift</div>
                <div className={styles.scoreItemValue}>{Math.round(selectedIssue.scores.sentimentShift * 100)}%</div>
              </div>
              <div className={styles.scoreItem}>
                <div className={styles.scoreItemLabel}>Bron Diversiteit</div>
                <div className={styles.scoreItemValue}>{Math.round(selectedIssue.scores.sourceDiversity * 100)}%</div>
              </div>
              <div className={styles.scoreItem}>
                <div className={styles.scoreItemLabel}>Stakeholders</div>
                <div className={styles.scoreItemValue}>{Math.round(selectedIssue.scores.stakeholderCount * 100)}%</div>
              </div>
              <div className={styles.scoreItem}>
                <div className={styles.scoreItemLabel}>Momentum</div>
                <div className={styles.scoreItemValue}>{Math.round(selectedIssue.scores.momentum * 100)}%</div>
              </div>
            </div>
          </div>

          {/* Stakeholders */}
          {selectedIssue.stakeholders.length > 0 && (
            <div className={styles.card}>
              <h3>Betrokken Stakeholders</h3>
              <div className={styles.issueStakeholders}>
                {selectedIssue.stakeholders.map((sh, i) => (
                  <span key={i} className={styles.stakeholderTag}>
                    {sh.name} ({sh.type})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Source Breakdown */}
          {selectedIssue.sourceBreakdown.length > 0 && (
            <div className={styles.card}>
              <h3>Bronverdeling</h3>
              <div className={styles.barChart}>
                {selectedIssue.sourceBreakdown.map((sb, i) => (
                  <div key={i} className={styles.barItem}>
                    <span className={styles.barLabel}>{SOURCE_LABELS[sb.source] || sb.source}</span>
                    <div className={styles.barTrack}>
                      <div
                        className={styles.barFill}
                        style={{
                          width: `${(sb.count / Math.max(...selectedIssue.sourceBreakdown.map(s => s.count))) * 100}%`,
                          backgroundColor: '#E6007E'
                        }}
                      />
                    </div>
                    <span className={styles.barValue}>{sb.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status & Notes */}
          <div className={styles.card}>
            <h3>Status & Notities</h3>
            <div style={{ marginBottom: '1rem' }}>
              <select
                className={styles.statusSelect}
                value={selectedIssue.status}
                onChange={(e) => handleIssueStatusChange(selectedIssue._id, e.target.value)}
              >
                <option value="active">Actief</option>
                <option value="monitoring">Monitoring</option>
                <option value="resolved">Opgelost</option>
                <option value="dismissed">Afgewezen</option>
              </select>
            </div>
            <textarea
              className={styles.notesArea}
              placeholder="Voeg notities toe..."
              defaultValue={selectedIssue.notes || ''}
              onBlur={async (e) => {
                if (e.target.value !== (selectedIssue.notes || '')) {
                  try {
                    await issueRadarAPI.updateIssue(project.id, selectedIssue._id, { notes: e.target.value });
                  } catch (error) {
                    console.error('Failed to save notes:', error);
                  }
                }
              }}
            />
          </div>

          {/* Related Signals */}
          {relatedSignals.length > 0 && (
            <div className={styles.card}>
              <h3>Gerelateerde Signalen ({relatedSignals.length})</h3>
              <table className={styles.signalsTable}>
                <thead>
                  <tr>
                    <th>Bron</th>
                    <th>Titel</th>
                    <th>Sentiment</th>
                    <th>Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {relatedSignals.map((signal) => (
                    <tr key={signal._id}>
                      <td>
                        <span className={styles.sourceTag}>{SOURCE_LABELS[signal.source] || signal.source}</span>
                      </td>
                      <td>
                        {signal.url ? (
                          <a href={signal.url} target="_blank" rel="noopener noreferrer">{signal.title || signal.text.substring(0, 80)}</a>
                        ) : (
                          signal.title || signal.text.substring(0, 80)
                        )}
                      </td>
                      <td>
                        <span
                          className={styles.sentimentDot}
                          style={{ backgroundColor: getSentimentColor(signal.sentimentScore) }}
                        />
                        {signal.sentimentScore > 0 ? '+' : ''}{signal.sentimentScore.toFixed(2)}
                      </td>
                      <td>{new Date(signal.timestamp).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.issueRadar}>
      <div className={styles.header}>
        <h1>Issue Radar</h1>
        <div className={styles.headerActions}>
          <button
            className={styles.syncButton}
            onClick={handleSync}
            disabled={syncing}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className={syncing ? styles.spinning : ''}
            >
              <path d="M21 2v6h-6M3 22v-6h6M21 13a9 9 0 01-15.36 5.36M3 11a9 9 0 0115.36-5.36" />
            </svg>
            {syncing ? 'Synchroniseren...' : 'Sync Bronnen'}
          </button>
        </div>
      </div>

      {/* Sync Result Toast */}
      {syncResult && (
        <div className={styles.syncToast}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {syncResult.includes('mislukt') ? (
              <><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>
            ) : (
              <><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>
            )}
          </svg>
          {syncResult}
        </div>
      )}

      {/* Tab Navigation */}
      <div className={styles.tabNav}>
        <button
          className={`${styles.tabButton} ${activeTab === 'overview' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overzicht
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'issues' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('issues')}
        >
          Issues ({issues.length})
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'signals' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('signals')}
        >
          Signalen ({overview?.totalSignals || 0})
        </button>
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && overview && (
        <>
          {/* Stats Cards */}
          <div className={styles.overviewGrid}>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Totaal Signalen</div>
              <div className={styles.statValue}>{overview.totalSignals}</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Actieve Issues</div>
              <div className={styles.statValue}>{overview.activeIssues}</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Overall Risico</div>
              <div className={`${styles.statValueRisk} ${getRiskClass(overview.overallRisk)}`}>
                {overview.overallRisk}
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Risiconiveau</div>
              <div className={`${styles.statValueRisk} ${getRiskClass(overview.overallRisk)}`}>
                {overview.overallRisk >= 80 ? 'Acuut' :
                 overview.overallRisk >= 60 ? 'Verhoogd' :
                 overview.overallRisk >= 30 ? 'Aandacht' : 'Laag'}
              </div>
            </div>
          </div>

          {/* Alert for high risk */}
          {overview.overallRisk >= 60 && (
            <div className={`${styles.card} ${overview.overallRisk >= 80 ? styles.alertCardAcute : styles.alertCard}`}>
              <div className={`${styles.alertIcon} ${overview.overallRisk >= 80 ? styles.alertIconAcute : ''}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div className={styles.alertContent}>
                <div className={styles.alertTitle}>
                  {overview.overallRisk >= 80 ? 'Acuut risico gedetecteerd' : 'Verhoogd risico'}
                </div>
                <div className={styles.alertMessage}>
                  Er zijn {overview.activeIssues} actieve issues met een gemiddelde risicoscore van {overview.overallRisk}.
                  {overview.topIssues[0] && ` Hoogste risico: "${overview.topIssues[0].title}" (score ${overview.topIssues[0].riskScore}).`}
                </div>
              </div>
            </div>
          )}

          {/* Charts */}
          <div className={styles.chartsGrid}>
            {/* Sentiment Trend */}
            <div className={styles.chartContainer}>
              <h3>Sentiment Trend (14 dagen)</h3>
              {overview.sentimentTrend.length > 0 ? (
                <>
                  <div className={styles.trendChart}>
                    {overview.sentimentTrend.map((point, i) => {
                      const height = Math.abs(point.sentiment) * 100;
                      return (
                        <div
                          key={i}
                          className={styles.trendBar}
                          style={{
                            height: `${Math.max(10, height)}%`,
                            backgroundColor: getSentimentColor(point.sentiment)
                          }}
                          data-tooltip={`${point.date}: ${point.sentiment > 0 ? '+' : ''}${point.sentiment.toFixed(2)} (${point.volume} signalen)`}
                          title={`${point.date}: ${point.sentiment > 0 ? '+' : ''}${point.sentiment.toFixed(2)}`}
                        />
                      );
                    })}
                  </div>
                  <div className={styles.trendLabels}>
                    <span>{overview.sentimentTrend[0]?.date}</span>
                    <span>{overview.sentimentTrend[overview.sentimentTrend.length - 1]?.date}</span>
                  </div>
                </>
              ) : (
                <p style={{ color: '#999', textAlign: 'center', padding: '2rem 0' }}>Nog geen data beschikbaar</p>
              )}
            </div>

            {/* Source Breakdown */}
            <div className={styles.chartContainer}>
              <h3>Bronverdeling</h3>
              {overview.sourceBreakdown.length > 0 ? (
                <div className={styles.barChart}>
                  {overview.sourceBreakdown.map((item, i) => {
                    const maxCount = Math.max(...overview.sourceBreakdown.map(s => s.count));
                    const colors = ['#E6007E', '#7B1FA2', '#1565C0', '#00897B', '#E65100', '#F9A825', '#546E7A', '#D81B60'];
                    return (
                      <div key={i} className={styles.barItem}>
                        <span className={styles.barLabel}>{SOURCE_LABELS[item.source] || item.source}</span>
                        <div className={styles.barTrack}>
                          <div
                            className={styles.barFill}
                            style={{
                              width: `${(item.count / maxCount) * 100}%`,
                              backgroundColor: colors[i % colors.length]
                            }}
                          />
                        </div>
                        <span className={styles.barValue}>{item.count}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: '#999', textAlign: 'center', padding: '2rem 0' }}>Nog geen data beschikbaar</p>
              )}
            </div>
          </div>

          {/* Top Issues */}
          {overview.topIssues.length > 0 && (
            <div className={styles.card}>
              <h2>Top Issues</h2>
              <div className={styles.issueList}>
                {overview.topIssues.map((issue) => (
                  <div
                    key={issue._id}
                    className={styles.issueItem}
                    onClick={() => handleIssueClick(issue)}
                  >
                    <div
                      className={styles.issueRiskScore}
                      style={{ backgroundColor: getRiskColor(issue.riskScore) }}
                    >
                      {issue.riskScore}
                    </div>
                    <div className={styles.issueInfo}>
                      <div className={styles.issueTitle}>{issue.title}</div>
                      <div className={styles.issueMeta}>
                        <span className={styles.issueLifecycle}>
                          <span
                            className={styles.lifecycleDot}
                            style={{ backgroundColor: LIFECYCLE_COLORS[issue.lifecycle] }}
                          />
                          {LIFECYCLE_LABELS[issue.lifecycle]}
                        </span>
                        <span>{issue.signalCount} signalen</span>
                        <span>{issue.stakeholders.length} stakeholders</span>
                      </div>
                    </div>
                    <span className={`${styles.riskBadge} ${getRiskBadgeClass(issue.riskLevel)}`}>
                      {issue.riskLevel === 'acute' ? 'Acuut' :
                       issue.riskLevel === 'elevated' ? 'Verhoogd' :
                       issue.riskLevel === 'attention' ? 'Aandacht' : 'Laag'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {overview.totalSignals === 0 && (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#CCC" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4M12 8h.01"/>
                </svg>
              </div>
              <p>Nog geen signalen opgehaald. Klik op "Sync Bronnen" om te beginnen.</p>
            </div>
          )}
        </>
      )}

      {/* ISSUES TAB */}
      {activeTab === 'issues' && (
        <div className={styles.card}>
          <h2>Alle Issues ({issues.length})</h2>
          {issues.length > 0 ? (
            <div className={styles.issueList}>
              {issues.map((issue) => (
                <div
                  key={issue._id}
                  className={styles.issueItem}
                  onClick={() => handleIssueClick(issue)}
                >
                  <div
                    className={styles.issueRiskScore}
                    style={{ backgroundColor: getRiskColor(issue.riskScore) }}
                  >
                    {issue.riskScore}
                  </div>
                  <div className={styles.issueInfo}>
                    <div className={styles.issueTitle}>{issue.title}</div>
                    <div className={styles.issueMeta}>
                      <span className={styles.issueLifecycle}>
                        <span
                          className={styles.lifecycleDot}
                          style={{ backgroundColor: LIFECYCLE_COLORS[issue.lifecycle] }}
                        />
                        {LIFECYCLE_LABELS[issue.lifecycle]}
                      </span>
                      <span>{issue.signalCount} signalen</span>
                      {issue.latestSignalDate && (
                        <span>
                          Laatste: {new Date(issue.latestSignalDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                    </div>
                    {issue.stakeholders.length > 0 && (
                      <div className={styles.issueStakeholders}>
                        {issue.stakeholders.slice(0, 4).map((sh, i) => (
                          <span key={i} className={styles.stakeholderTag}>{sh.name}</span>
                        ))}
                        {issue.stakeholders.length > 4 && (
                          <span className={styles.stakeholderTag}>+{issue.stakeholders.length - 4}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <span className={`${styles.riskBadge} ${getRiskBadgeClass(issue.riskLevel)}`}>
                    {issue.riskLevel === 'acute' ? 'Acuut' :
                     issue.riskLevel === 'elevated' ? 'Verhoogd' :
                     issue.riskLevel === 'attention' ? 'Aandacht' : 'Laag'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.empty}>
              <p>Nog geen issues gedetecteerd. Sync eerst de bronnen.</p>
            </div>
          )}
        </div>
      )}

      {/* SIGNALS TAB */}
      {activeTab === 'signals' && (
        <>
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {['all', 'openraadsinformatie', 'woo_documenten', 'gdelt', 'petities', 'rivm', 'knmi'].map(source => (
              <button
                key={source}
                onClick={() => setSignalFilter(source)}
                style={{
                  padding: '0.4rem 0.8rem',
                  borderRadius: '16px',
                  border: signalFilter === source ? '2px solid #E6007E' : '1px solid #DDD',
                  background: signalFilter === source ? '#FDE4EF' : 'white',
                  color: signalFilter === source ? '#E6007E' : '#757575',
                  fontSize: '0.8rem',
                  fontWeight: signalFilter === source ? 600 : 400,
                  cursor: 'pointer'
                }}
              >
                {source === 'all' ? 'Alle bronnen' : SOURCE_LABELS[source] || source}
              </button>
            ))}
          </div>

          <div className={styles.card}>
            <h2>Signalen</h2>
            {signals.length > 0 ? (
              <table className={styles.signalsTable}>
                <thead>
                  <tr>
                    <th>Bron</th>
                    <th>Titel</th>
                    <th>Sentiment</th>
                    <th>Stakeholder</th>
                    <th>Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {signals.map((signal) => (
                    <tr key={signal._id}>
                      <td>
                        <span className={styles.sourceTag}>{SOURCE_LABELS[signal.source] || signal.source}</span>
                      </td>
                      <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {signal.url ? (
                          <a href={signal.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1565C0' }}>
                            {signal.title || signal.text.substring(0, 80)}
                          </a>
                        ) : (
                          signal.title || signal.text.substring(0, 80)
                        )}
                      </td>
                      <td>
                        <span
                          className={styles.sentimentDot}
                          style={{ backgroundColor: getSentimentColor(signal.sentimentScore) }}
                        />
                        {signal.sentimentScore > 0 ? '+' : ''}{signal.sentimentScore.toFixed(2)}
                      </td>
                      <td style={{ fontSize: '0.8rem', color: '#757575' }}>
                        {signal.stakeholder || '-'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {new Date(signal.timestamp).toLocaleDateString('nl-NL', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={styles.empty}>
                <p>Nog geen signalen gevonden. Sync eerst de bronnen.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
